import type { RecordDetailHeaderPresentation } from "@kontextwerk/web-sdk/ui/layout/recordDetail/types"
import {
  mdiAccountOutline,
  mdiCalendarClockOutline,
  mdiClipboardTextOutline,
  mdiMapMarkerOutline,
} from "@mdi/js"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../../../../_shared/_logic/communication-presentation"
import {
  eventTypeLabels,
  type EmployeeOption,
} from "../../../../../../../../../../_shared/_logic/form-state"
import { resolveScheduleStatusDefinition } from "../../../../../../../../../../_shared/_logic/status-definitions"
import {
  findPropertyUnitById,
  formatPropertyUnitIdentifier,
  formatPropertyUnitLabel,
} from "../../../../../../../../../../_shared/_logic/unit-labels"
import { internalOrderTypeLabel } from "../../../../../../../../../../_shared/_logic/order-type-labels"
import {
  dateOnlyInTimezone,
  DEFAULT_ORGANIZATION_TIMEZONE,
  formatDateOnlyDisplay,
} from "../../../../../../../../../_shared/_logic/timezones"

const text = (value: unknown): string => String(value ?? "").trim()

const formatTimeRange = (event: ScheduleEvent): string | null => {
  const start = new Date(event.start_at)
  const end = new Date(event.end_at)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null

  const date = (value: Date) =>
    value.toLocaleDateString("de-DE", {
      timeZone: "Europe/Berlin",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  const time = (value: Date) =>
    value.toLocaleTimeString("de-DE", {
      timeZone: "Europe/Berlin",
      hour: "2-digit",
      minute: "2-digit",
    })

  if (event.all_day) {
    const firstDay = dateOnlyInTimezone(start, DEFAULT_ORGANIZATION_TIMEZONE)
    // Calendar intervals have an exclusive end, including across DST changes.
    const lastDay = dateOnlyInTimezone(new Date(end.getTime() - 1), DEFAULT_ORGANIZATION_TIMEZONE)

    if (!firstDay || !lastDay || end <= start) return null

    return firstDay === lastDay
      ? `Ganztägig am ${formatDateOnlyDisplay(firstDay)}`
      : `Ganztägig vom ${formatDateOnlyDisplay(firstDay)} bis ${formatDateOnlyDisplay(lastDay)}`
  }

  return date(start) === date(end)
    ? `${date(start)}, ${time(start)} bis ${time(end)} Uhr`
    : `${date(start)}, ${time(start)} bis ${date(end)}, ${time(end)} Uhr`
}

const unitDescription = (property: Property | null, workOrder: WorkOrder | null) => {
  if (!property && !workOrder?.property_unit_id) return undefined

  if (!workOrder?.property_unit_id) return "Gebäudeeinheit"

  const unit = findPropertyUnitById(property, workOrder.property_unit_id)

  return unit
    ? formatPropertyUnitIdentifier(unit)
    : text(workOrder.property_unit_label)
      ? formatPropertyUnitLabel(workOrder.property_unit_label)
      : undefined
}

/** Present only confirmed event data; editing drafts never enter the record identity. */
export const buildScheduleEventHeader = ({
  event,
  employees,
  workOrder,
  property,
}: {
  event: ScheduleEvent
  employees: EmployeeOption[]
  workOrder: WorkOrder | null
  property: Property | null
}): { title: string; presentation: RecordDetailHeaderPresentation } => {
  const appointment = event.event_type === "APPOINTMENT"
  const typeLabel = eventTypeLabels[event.event_type] ?? "Kalendereintrag"
  const title =
    (appointment ? internalOrderTypeLabel(workOrder) : null) || text(event.title) || typeLabel
  const status = appointment
    ? buildScheduleEventCommunicationTagPresentation({ status: event.status, event }).definition
    : resolveScheduleStatusDefinition(event.status)
  const fields: RecordDetailHeaderPresentation["fields"] = []
  const period = formatTimeRange(event)

  if (period)
    fields.push({ id: "time", label: "Zeitpunkt", value: period, icon: mdiCalendarClockOutline })

  const employee =
    text(employees.find((entry) => entry.id === event.employee_id)?.label) ||
    text(event.employee_name)

  if (employee)
    fields.push({ id: "employee", label: "Mitarbeiter", value: employee, icon: mdiAccountOutline })

  const address = property
    ? [
        [property.street, property.house_number].map(text).filter(Boolean).join(" "),
        [property.zip, property.city].map(text).filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(", ")
    : ""
  const location = address || text(event.location_text)

  if (location) {
    fields.push({
      id: "property",
      label: "Liegenschaft",
      value: location,
      description: unitDescription(property, workOrder),
      icon: mdiMapMarkerOutline,
    })
  }

  if (workOrder) {
    fields.push({
      id: "work-order",
      label: "Auftrag",
      value: text(workOrder.order_number) ? `Auftrag ${text(workOrder.order_number)}` : "Auftrag",
      icon: mdiClipboardTextOutline,
    })
  }

  return {
    title,
    presentation: {
      icon: mdiCalendarClockOutline,
      status: status
        ? {
            label: status.label,
            tone:
              status.color === "emerald" || status.color === "forest"
                ? "success"
                : status.color === "orange"
                  ? "warning"
                  : status.color === "rose"
                    ? "danger"
                    : "neutral",
          }
        : undefined,
      fields,
    },
  }
}
